import { motion } from "framer-motion";
import { Baby, Cake, CalendarCheck, Car, Gift, Heart, PartyPopper, PhoneCall, Sparkles } from "lucide-react";
import Logo from "./Logo";
import { waLink } from "../data/site";

const ease = [0.16, 1, 0.3, 1];
const rise = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.85, ease } }),
};

const ORBIT_ICONS = [Cake, Gift, Car, Baby, Heart, Sparkles];

const BENEFITS = [
  {
    icon: PhoneCall,
    title: "One call, everything arranged",
    text: "Décor, cake, return gifts, car and room styling — every essential under one roof, so you never juggle ten different vendors.",
  },
  {
    icon: PartyPopper,
    title: "The host just hosts",
    text: "You enjoy your own celebration with your guests while our team quietly handles planning, setup and cleanup.",
  },
  {
    icon: CalendarCheck,
    title: "Zero last-minute panic",
    text: "Every detail is planned days in advance and our team arrives early — the venue is ready before your first guest walks in.",
  },
  {
    icon: Cake,
    title: "Birthday specialists",
    text: "Themed setups, balloon art and designer cakes are our home ground — our speciality and our first love.",
  },
];

function Orbit() {
  return (
    <div data-testid="why-us-orbit" className="relative mx-auto h-72 w-72 sm:h-80 sm:w-80">
      <div className="absolute inset-6 rounded-full border border-dashed border-gold/30" />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        {ORBIT_ICONS.map((Icon, i) => {
          const angle = (i / ORBIT_ICONS.length) * 360;
          return (
            <div
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{ transform: `rotate(${angle}deg) translateX(138px) rotate(${-angle}deg)` }}
            >
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
                className="-ml-6 -mt-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-night text-gold shadow-[0_0_24px_-6px_rgba(201,162,75,0.7)]">
                  <Icon className="h-5 w-5" />
                </span>
              </motion.div>
            </div>
          );
        })}
      </motion.div>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="absolute h-28 w-28 animate-ping rounded-full bg-gold/25 [animation-duration:3s]" />
        <span className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-gold bg-cream shadow-[0_0_60px_-12px_rgba(201,162,75,0.8)]">
          <Logo className="h-14 w-14" />
        </span>
      </div>
    </div>
  );
}

export default function WhyUs() {
  return (
    <section id="why-us" data-testid="why-us-section" className="grain relative overflow-hidden border-y border-gold/25 bg-night py-24 text-cream sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
        >
          <div>
            <motion.p variants={rise} custom={0} className="mb-5 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">
              Why Choose Us
            </motion.p>
            <motion.h2 variants={rise} custom={1} className="font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
              You bring the guests.
              <br />
              <span className="font-script gold-foil-text pr-2 text-[1.15em]">We bring</span>
              <span className="italic"> everything else.</span>
            </motion.h2>
            <motion.p variants={rise} custom={2} className="mt-5 max-w-md text-base leading-relaxed text-cream/60 sm:text-lg">
              Planning an event usually means chasing decorators, bakers, gift shops and drivers.
              With Vayu, one call covers it all — the host never runs around, and never misses
              their own party.
            </motion.p>
            <motion.a
              variants={rise}
              custom={3}
              data-testid="why-us-whatsapp-button"
              href={waLink("Hello Vayu Celebrations! I want you to handle my event end to end.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-night transition-all hover:-translate-y-0.5 hover:bg-gold-light"
            >
              <PhoneCall className="h-4 w-4" /> Hand It All To Us
            </motion.a>
          </div>

          <motion.div variants={rise} custom={2} className="flex justify-center py-4">
            <Orbit />
          </motion.div>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              data-testid={`why-us-benefit-${i + 1}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.1, duration: 0.85, ease }}
              className="group relative overflow-hidden rounded-xl border border-cream/10 bg-cream/[0.04] p-6 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/50 hover:bg-cream/[0.07]"
            >
              <span className="absolute right-4 top-4 font-serif text-sm italic text-gold/50">
                {String(i + 1).padStart(2, "0")} —
              </span>
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-night">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-serif text-xl font-semibold text-cream">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-cream/55">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
